import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, FileText, Flag, Info, Lock, MapPin, UserRound } from 'lucide-react';
import { DateInput } from '../DateTimeInput';
import { StatusBar, xbStatusColor } from './StatusBar';
import { useAuth } from '../../context/AuthContext';
import { crossBorderAPI } from '../../api';
import { RecordMilestoneDialog, CorrectMilestoneDialog, ReviewDocumentsDialog, OverrideStageDialog } from './CrossBorderActions';

const DOC_BADGE = {
  Missing: { bg: '#fde2e2', fg: '#8a1c1c', Icon: AlertTriangle },
  Partial: { bg: '#fdebc8', fg: '#7a4b00', Icon: Clock },
  Complete: { bg: '#d2f0dc', fg: '#0c4a25', Icon: CheckCircle2 },
  'Not Applicable': { bg: '#e6e8ee', fg: '#3b4252', Icon: Info },
};
const MS_LABEL = { origin_arrival: 'Arrive origin side', origin_departure: 'Depart origin side', crossing_confirmed_at: 'Crossing confirmed', destination_arrival: 'Arrive destination side', destination_departure: 'Depart destination side', expected_crossing_at: 'Expected crossing' };

const Line = ({ label, children, testId }) => (
  <div className="lw-row" data-testid={testId}>
    <span className="lw-label w80">{label}</span>
    <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>{children}</span>
  </div>
);

const Badge = ({ bg, fg, Icon, children, testId }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, color: fg, borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 700 }} data-testid={testId}>
    {Icon && <Icon size={12} />}{children}
  </span>
);

const Timeline = ({ crossings }) => (
  <div style={{ display: 'grid', gap: 6, padding: '4px 0' }} data-testid="cb-timeline">
    {crossings.map((c, i) => (
      <div key={c.id} style={{ border: '1px solid var(--tms-border)', borderRadius: 6, padding: '6px 8px', fontSize: 11 }} data-testid={`cb-crossing-${i}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
          <span><MapPin size={11} style={{ display: 'inline', marginRight: 3 }} />Crossing {i + 1}: {c.origin_country} → {c.destination_country}{c.location_name ? ` · ${c.location_name}` : ''}</span>
          <span style={{ color: 'var(--lw-muted)' }}>{c.complete ? 'Complete' : c.indicator || c.stage || 'No milestones yet'}</span>
        </div>
        <table style={{ width: '100%', marginTop: 4 }}>
          <tbody>
            {Object.entries(MS_LABEL).map(([k, label]) => {
              const m = c.milestones[k];
              const ev = (c.events || []).filter(e => !e.voided).find(e => e.at === m.utc);
              return (
                <tr key={k} style={{ color: m.utc ? 'inherit' : 'var(--lw-muted)' }}>
                  <td style={{ width: 130 }}>{label}{k === 'expected_crossing_at' && <span style={{ fontStyle: 'italic' }}> (ETA)</span>}</td>
                  <td>{m.utc ? <>{m.local}<span style={{ color: 'var(--lw-muted)' }}> · {m.central} CT</span></> : '—'}</td>
                  <td style={{ textAlign: 'right', color: 'var(--lw-muted)' }}>{ev?.user_name || ''}{ev?.corrections?.length ? ' · corrected' : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 12, marginTop: 4, color: 'var(--lw-muted)' }}>
          <span title="Time waiting on the origin side of the border">Origin side: <b>{c.time_origin_side.label}</b></span>
          <span title="Time waiting on the destination side of the border">Destination side: <b>{c.time_destination_side.label}</b></span>
          <span title="Origin-side arrival → destination-side departure">Total crossing: <b>{c.total_crossing.label}</b>{c.total_crossing.open ? ' (running)' : ''}</span>
        </div>
      </div>
    ))}
  </div>
);

export const DocsCrossBorderPanel = ({ load, onChange, onCreateBOL, onCreateRC, onServerLoad }) => {
  const { isAdmin } = useAuth();
  const set = (field) => (e) => onChange(field, e.target.value);
  const cb = load.cb;
  const [meta, setMeta] = useState(null);
  const [dialog, setDialog] = useState(null); // 'milestone' | 'correct' | 'docs' | 'override'
  const [milestoneType, setMilestoneType] = useState('origin_arrival');
  const [showTimeline, setShowTimeline] = useState(false);

  useEffect(() => {
    if (!load.cross_border || meta) return;
    crossBorderAPI.meta().then(r => setMeta(r.data)).catch(() => {});
  }, [load.cross_border, meta]);

  const openMilestone = (type) => { setMilestoneType(type); setDialog('milestone'); };
  const docBadge = DOC_BADGE[cb?.docs?.state] || DOC_BADGE['Not Applicable'];
  const isNotStarted = cb?.display === 'Not Started';
  const saved = !!cb; // cb summary is only present for a saved XB load

  return (
    <div className="lw-panel" data-testid="lw-panel-docs-cb">
      <StatusBar
        left={
          <>
            <span>Documents &amp; Cross Border</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, marginLeft: 8, cursor: 'pointer' }} onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" className="lw-checkbox" style={{ width: 13, height: 13 }} checked={!!load.cross_border}
                onChange={(e) => { onChange('cross_border', e.target.checked); if (!e.target.checked) onChange('cb_status', null); }}
                data-testid="lw-cb-cross-border-toggle" />
              XB
            </label>
          </>
        }
        right={load.cross_border ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }} title="Cross-Border Stage — derived from recorded border milestones" data-testid="lw-xb-stage">
            {cb?.override && <Lock size={12} title="Manually overridden" data-testid="lw-xb-override-indicator" />}
            Cross-Border Stage: {cb?.display || (load.cb_status || 'Not Started')}
          </span>
        ) : null}
        color={load.cross_border && cb?.stage && !isNotStarted ? xbStatusColor(cb.stage) : 'lavender'}
        testId="lw-statusbar-docs-cb"
      />

      {load.cross_border && !saved && (
        <div className="lw-row" style={{ fontSize: 11, color: 'var(--lw-muted)' }} data-testid="cb-save-first">Save the load to start tracking border milestones.</div>
      )}

      {load.cross_border && cb && (
        <>
          <Line label="Stage" testId="cb-stage-line">
            <strong>{cb.display}</strong>
            {cb.indicator && <Badge bg="#fdebc8" fg="#7a4b00" Icon={cb.source === 'legacy' ? Flag : Clock} testId="cb-stage-indicator">{cb.indicator}</Badge>}
            {cb.override && <Badge bg="#e6e8ee" fg="#3b4252" Icon={Lock} testId="cb-override-badge">Override by {cb.override.user_name}</Badge>}
            {cb.direction_label && <span style={{ color: 'var(--lw-muted)' }}>· Route {cb.direction_label}{cb.expected_crossings.length > 1 ? ` (${cb.expected_crossings.length} crossings)` : ''}</span>}
          </Line>
          <Line label="Documents" testId="cb-docs-line">
            <Badge {...docBadge} testId="cb-docs-badge">{cb.docs.state}</Badge>
            <span style={{ color: 'var(--lw-muted)' }}>{cb.docs.summary.replace(/^[^—]+(— )?/, '') || (cb.docs.state === 'Complete' ? 'All required documents valid' : 'No documents required for this route')}</span>
            <button className="lw-link" onClick={() => setDialog('docs')} data-testid="cb-btn-review-docs"><FileText size={11} style={{ display: 'inline' }} /> Review</button>
          </Line>
          <Line label="Border time" testId="cb-border-time-line">
            {cb.time_at_border
              ? <span title="Time at the current border side (stops at that side's departure)">At Border {cb.time_at_border.side}: <strong>{cb.time_at_border.label}</strong>{cb.time_at_border.open ? ' (running)' : ''}</span>
              : <span style={{ color: 'var(--lw-muted)' }}>Time at current side: Unavailable</span>}
            <span style={{ color: 'var(--lw-muted)' }}>· Total crossing: <strong>{cb.total_crossing.label}</strong>{cb.total_crossing.open ? ' (running)' : ''}</span>
            {cb.overdue && <Badge bg="#fde2e2" fg="#8a1c1c" Icon={AlertTriangle} testId="cb-overdue-badge">Overdue at border</Badge>}
          </Line>
          <Line label="Crossing ETA" testId="cb-eta-line">
            {cb.eta ? <span>{cb.eta.local}{cb.eta.central && !cb.eta.legacy ? <span style={{ color: 'var(--lw-muted)' }}> · {cb.eta.central} CT</span> : null}{cb.eta.legacy ? <span style={{ color: 'var(--lw-muted)' }}> (date only)</span> : null}</span> : <span style={{ color: 'var(--lw-muted)' }}>Not set</span>}
            <button className="lw-link" onClick={() => openMilestone('expected_crossing')} data-testid="cb-btn-set-eta">Set ETA</button>
          </Line>
          <Line label="Next action" testId="cb-next-action-line">
            <strong>{cb.next_action}</strong>
            <span style={{ color: 'var(--lw-muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><UserRound size={11} /> {cb.owner || 'Unassigned'}</span>
          </Line>

          {cb.conflicts.length > 0 && (
            <div style={{ display: 'grid', gap: 3, padding: '2px 0' }} data-testid="cb-conflicts">
              {cb.conflicts.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, fontSize: 11, color: c.severity === 'error' ? '#8a1c1c' : c.severity === 'warning' ? '#7a4b00' : 'var(--lw-muted)' }} data-testid={`cb-conflict-${c.code}`}>
                  {c.severity === 'error' ? <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} /> : c.severity === 'warning' ? <Flag size={12} style={{ flexShrink: 0, marginTop: 1 }} /> : <Info size={12} style={{ flexShrink: 0, marginTop: 1 }} />}
                  <span><strong>{c.message}</strong> {c.action}</span>
                </div>
              ))}
            </div>
          )}

          <div className="lw-btnrow" style={{ flexWrap: 'wrap', gap: 4 }}>
            <button className="lw-btn primary" onClick={() => openMilestone('origin_arrival')} disabled={isNotStarted} title={isNotStarted ? 'Record the pickup departure first' : ''} data-testid="cb-btn-arrival">Record Border Arrival</button>
            <button className="lw-btn" onClick={() => openMilestone('origin_departure')} disabled={isNotStarted} data-testid="cb-btn-departure">Record Border Departure</button>
            <button className="lw-btn" onClick={() => openMilestone('crossing_confirmed')} disabled={isNotStarted} data-testid="cb-btn-confirm">Confirm Crossing</button>
            <button className="lw-btn" onClick={() => setDialog('correct')} data-testid="cb-btn-correct">Correct Milestone</button>
            {isAdmin() && <button className="lw-btn" onClick={() => setDialog('override')} data-testid="cb-btn-override">{cb.override ? 'Return to Automatic' : 'Override Stage'}</button>}
            <button className="lw-link" onClick={() => setShowTimeline(v => !v)} data-testid="cb-btn-timeline">{showTimeline ? 'Hide' : 'Show'} border timeline ({cb.crossings.length})</button>
          </div>
          {showTimeline && (cb.crossings.length ? <Timeline crossings={cb.crossings} /> : <div style={{ fontSize: 11, color: 'var(--lw-muted)', padding: '2px 0' }}>No crossings recorded yet — record the first border arrival to start the timeline.</div>)}
          {(load.border_arrival_date_mx || load.border_arrival_date_us) && (
            <Line label="Legacy dates" testId="cb-legacy-dates"><span style={{ color: 'var(--lw-muted)' }}>Border MX {load.border_arrival_date_mx || '—'} · Border US {load.border_arrival_date_us || '—'} (date only, pre-timeline)</span></Line>
          )}
          <div className="lw-sep" />
        </>
      )}

      <div className="lw-row">
        <span className="lw-label w80">SCAC</span>
        <input className="lw-input narrow" value={load.transfer_scac || ''} onChange={set('transfer_scac')} data-testid="lw-scac" />
        <span className="lw-label" style={{ marginLeft: 6 }}>CAAT</span>
        <input className="lw-input narrow" value={load.transfer_caat || ''} onChange={set('transfer_caat')} data-testid="lw-caat" />
      </div>
      <div className="lw-row">
        <span className="lw-label w80">Transfer</span>
        <input className="lw-input" value={load.transfer_name || ''} onChange={set('transfer_name')} placeholder="Transfer / drayage name" data-testid="lw-transfer-name" />
      </div>
      <div className="lw-row">
        <span className="lw-label w80">Docs Due</span>
        <DateInput value={load.crossing_docs_due_date || ''} onChange={(e) => onChange('crossing_docs_due_date', e.target.value)} className="lw-input tiny" data-testid="lw-docs-due" />
        <span className="lw-label" style={{ marginLeft: 6 }}>Received</span>
        <DateInput value={load.crossing_docs_received_date || ''} onChange={(e) => onChange('crossing_docs_received_date', e.target.value)} className="lw-input tiny" data-testid="lw-docs-received" />
      </div>

      <div className="lw-btnrow" style={{ justifyContent: 'space-between' }}>
        <button className="lw-btn primary" style={{ flex: '0 1 45%' }} onClick={onCreateBOL} data-testid="lw-btn-create-bol">Create BOL</button>
        <button className="lw-btn" style={{ flex: '0 1 45%' }} onClick={onCreateRC} data-testid="lw-btn-create-rc">Create RC</button>
      </div>

      {cb && (
        <>
          <RecordMilestoneDialog open={dialog === 'milestone'} onOpenChange={(o) => !o && setDialog(null)} load={load} meta={meta} initialType={milestoneType} onSaved={onServerLoad} />
          <CorrectMilestoneDialog open={dialog === 'correct'} onOpenChange={(o) => !o && setDialog(null)} load={load} onSaved={onServerLoad} />
          <ReviewDocumentsDialog open={dialog === 'docs'} onOpenChange={(o) => !o && setDialog(null)} load={load} onSaved={onServerLoad} />
          <OverrideStageDialog open={dialog === 'override'} onOpenChange={(o) => !o && setDialog(null)} load={load} meta={meta} onSaved={onServerLoad} />
        </>
      )}
    </div>
  );
};
